import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import {
    isSelfServeCompany,
    isSelfServeViewer,
    isSelfServeViewerKnown,
    selfServeAccount,
    selfServeAllows,
    selfServeAllowsPage,
    selfServeShows,
    selfServeShowsPage,
} from '../utils/selfServe';
import { fetchSelfServeAccountSummary } from '../services/selfServeService';

/**
 * The self-serve state of the current company and viewer (src/utils/selfServe.js):
 *   company  the company in context is self-serve (rate and book on the server)
 *   viewer   the effective user is a self-serve member, not staff browsing a
 *            self-serve company: decided from the user's profile
 *            (users/{uid}.selfServeTenant, loaded by AuthContext)
 *   known    `viewer` is final (staff, the profile has loaded, or for a user
 *            object without the flag the company context has loaded); a load
 *            that the server refuses a member waits for it
 *   account  { tier, status, limits, canBook, pickups } or null
 *   allows(permission)  false for a surface hidden from a self-serve member;
 *                       call sites AND it onto their role check
 *   allowsPage(path)    false for a page hidden from a self-serve member that no
 *                       permission gates (SELF_SERVE_HIDDEN_PAGES: the API pages)
 *   shows(permission) / showsPage(path)
 *                       what a MENU offers: as allows / allowsPage, but a surface
 *                       hidden from members stays hidden until `known`, so a
 *                       member's header never flashes it while the company loads
 * Staff and every other company get company=false, viewer=false, allows()=true,
 * allowsPage()=true.
 */
export default function useSelfServe() {
    const { userRole, currentUser } = useAuth();
    const { companyData, loading: companyLoading } = useCompany() || {};
    const profileFlag = currentUser ? currentUser.selfServeTenant : undefined;
    return useMemo(() => {
        const profile = { selfServeTenant: profileFlag };
        const viewer = isSelfServeViewer({ userRole, companyData, profile });
        const account = selfServeAccount(companyData);
        const known = isSelfServeViewerKnown({ userRole, companyData, companyLoading, profile });
        return {
            company: isSelfServeCompany(companyData),
            viewer,
            known,
            account,
            companyDocId: companyData?.id || null,
            allows: (permission) => selfServeAllows(permission, { viewer }),
            allowsPage: (path) => selfServeAllowsPage(path, { viewer }),
            shows: (permission) => selfServeShows(permission, { viewer, known }),
            showsPage: (path) => selfServeShowsPage(path, { viewer, known }),
        };
    }, [userRole, profileFlag, companyData, companyLoading]);
}

/**
 * The server's account summary (getSelfServeAccountSummary) for a self-serve
 * company: limits, exposure, open invoices, today's bookings. Loads only when
 * `enabled`; `refreshKey` reloads it (e.g. after a booking).
 * @returns {{summary: object|null, loading: boolean, error: Error|null}}
 */
export function useSelfServeAccountSummary({ enabled, companyDocId, refreshKey = 0 }) {
    const [state, setState] = useState({ summary: null, loading: false, error: null });
    useEffect(() => {
        if (!enabled || !companyDocId) {
            setState({ summary: null, loading: false, error: null });
            return undefined;
        }
        let cancelled = false;
        setState((prev) => ({ ...prev, loading: true, error: null }));
        fetchSelfServeAccountSummary(companyDocId)
            .then((summary) => {
                if (!cancelled) setState({ summary: summary && summary.selfServe ? summary : null, loading: false, error: null });
            })
            .catch((error) => {
                if (!cancelled) setState({ summary: null, loading: false, error });
            });
        return () => { cancelled = true; };
    }, [enabled, companyDocId, refreshKey]);
    return state;
}
